import React, { useEffect, useState } from 'react';
import {
  Portal,
  Text,
  Stack,
  Tabs,
  Box,
  Button,
  Table,
  Dialog,
  Icon,
  Link,
  CloseButton,
  Image,
  Flex,
  ProgressCircle,
} from '@chakra-ui/react';
import { FaInfoCircle } from 'react-icons/fa';
import { useMap } from './map-context';
import { sectionLayer } from '@/geospatial';
import { transform } from 'ol/proj';
import { PhChunkObject } from '@/lib/types';
import { pointService } from '@/lib/services/pointService';
import { coordinateService } from '@/lib/services/coordinateService';
import { displayFeatureInfo } from '@/lib/utils/displayFeatureInfo';
import EnhancedSoilInfo from '@/components/soil-info/enhanced-soil-info';
import MapProgressBar from '../ui/map-progress-bar';
import DonuteChart from '../ui/donute-chart';

interface PointData {
  properties?: {
    filename?: string;
    datatype?: 'photo' | 'publication' | 'soilpit' | 'observation';
  };
  id?: string;
  geometry: {
    type: string;
  };
}

const MapDrawer: React.FC = () => {
  const colors = ['blue.500', 'orange.500', 'red.500', 'green.500', 'purple.500', 'yellow.500', 'pink.500', 'gray.500', 'teal.500', 'cyan.500'];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { map, clickedCoordinate, setClickedCoordinate, mouseCoordinate, scaleString, features, mapResolution } =
    useMap();
  const [polygonInfoProperties, setPolygonInfoProperties] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [soilPitId, setSoilPitId] = useState<string | null>(null);
  const [soilPitData, setSoilPitData] = useState<any>(null);
  const [showEnhancedInfo, setShowEnhancedInfo] = useState<boolean>(false);
  const [components, setComponents] = useState<any>([]);
  const [errataNote, setErrataNote] = useState<any>(null);
  const [ecoSiteInfo, setEcoSiteInfo] = useState<any>(null);
  const [surficialGeologyInfo, setSurficialGeologyInfo] = useState<any>(null);
  const [irrigDegreeoflimitaion, setIrrigDegreeoflimitaion] = useState<any>(null);
  const [ecoRegionInfo, setEcoRegionInfo] = useState<any>(null);
  const [phProportions, setPhProportions] = useState<PhChunkObject[]>([]);
  const [sectionInfo, setSectionInfo] = useState<any>(null);
  const [tab, setTab] = useState<string | null>(() => {
    const savedTab = localStorage.getItem('map-drawer-tab');
    return savedTab || "polygon";
  });

  const saveTabToStorage = (tabValue: string) => {
    try {
      localStorage.setItem('map-drawer-tab', tabValue);
    } catch (error) {
      console.warn('Failed to save tab to localStorage:', error);
    }
  };

  const handleClose = () => {
    setClickedCoordinate(null);
    clearState();
  };

  const clearState = () => {
    setIsLoading(false);
    setPolygonInfoProperties(null);
    setImageUrl(null);
    setPdfUrl(null);
    setSoilPitId(null);
    setSoilPitData(null);
    setShowEnhancedInfo(false);
    setComponents([]);
    setErrataNote(null);
    setEcoSiteInfo(null);
    setSurficialGeologyInfo(null);
    setIrrigDegreeoflimitaion(null);
    setEcoRegionInfo(null);
    setPhProportions([]);
    setSectionInfo(null);
  };

  const getImageFromService = async (filename: string): Promise<void> => {
    try {
      const blob = await pointService.getImage(filename);
      const imageUrl = URL.createObjectURL(blob);
      setImageUrl(imageUrl);
    } catch (error) {
      console.error('Error fetching image:', error);
    }
  };

  const getPdfFromService = async (filename: string): Promise<void> => {
    try {
      const blob = await pointService.getPdf(filename);
      const fileUrl = URL.createObjectURL(blob);
      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Error fetching PDF:', error);
    }
  };

  const getComponents = async (polygonId: string): Promise<void> => {
    try {
      const data = await pointService.getPolygonComponents(polygonId);
      setComponents(data.resource || []);
    } catch (error) {
      console.error('Error fetching components:', error);
      setComponents([]);
    }
  };

  useEffect(() => {
    const processFeature = async () => {
      try {
        if (clickedCoordinate) {
          try {
            const [ecoRegion, surficialGeo] = await Promise.allSettled([
              coordinateService.getEcoRegion(clickedCoordinate[1], clickedCoordinate[0]),
              coordinateService.getSurficialGeology(clickedCoordinate[1], clickedCoordinate[0]),
            ]);

            if (ecoRegion.status === 'fulfilled') {
              setEcoRegionInfo(ecoRegion.value);
            }

            if (surficialGeo.status === 'fulfilled') {
              setSurficialGeologyInfo(surficialGeo.value);
            }
          } catch (error) {
            console.error('Error fetching coordinate-based data:', error);
          }

          // Fetch Section GetFeatureInfo (Click LLD)
          try {
            if (map && mapResolution) {
              const view = map.getView();
              const projection = view.getProjection();
              const coordinate3857 = transform(
                [clickedCoordinate[0], clickedCoordinate[1]],
                'EPSG:4326',
                'EPSG:3857'
              );
              const src = sectionLayer.getSource();
              if (!src) {
                setSectionInfo(null);
                return;
              }
              // INFO_FORMAT application/json is required
              const url = src.getFeatureInfoUrl(
                coordinate3857 as [number, number],
                mapResolution,
                projection,
                { INFO_FORMAT: 'application/json' }
              );
              if (url) {
                const resp = await fetch(url);
                if (resp.ok) {
                  const data = await resp.json();
                  if (data && data.features && data.features.length > 0) {
                    setSectionInfo(data.features[0].properties || null);
                  } else {
                    setSectionInfo(null);
                  }
                } else {
                  setSectionInfo(null);
                }
              } else {
                setSectionInfo(null);
              }
            } else {
              setSectionInfo(null);
            }
          } catch (error) {
            console.error('Error fetching section GetFeatureInfo:', error);
            setSectionInfo(null);
          }
        }
      } catch {
        // Only clear state if there's an actual error, not just a new coordinate
        if (!clickedCoordinate) {
          clearState();
        }
      }
    };

    processFeature();
  }, [clickedCoordinate, map, mapResolution]);

  useEffect(() => {
    queueMicrotask(() => {
      setIsLoading(true);
      setPolygonInfoProperties(null);
    });

    if (features?.length > 0) {
      const feature = features[0];

      const processFeature = async () => {
        try {
          const processedInfo = await displayFeatureInfo(feature);

          setIsLoading(false);
          setPolygonInfoProperties(processedInfo.selectedFeature?.properties || null);
          setErrataNote(processedInfo.errataNote);
          setEcoSiteInfo(processedInfo.ecoSiteInfo);
          setIrrigDegreeoflimitaion(processedInfo.irrigDegreeoflimitaion);
          setSoilPitData(processedInfo.soilpitInfo);
          setPhProportions(processedInfo.phProportions);

          if (processedInfo.selectedFeature?.geometry?.type === 'Point') {
            const pointData = processedInfo.selectedFeature as unknown as PointData;
            if (pointData.properties?.datatype) {
              switch (pointData.properties.datatype) {
                case 'photo':
                  if (pointData.properties.filename) {
                    getImageFromService(pointData.properties.filename);
                  }
                  break;
                case 'publication':
                  if (pointData.properties.filename) {
                    getPdfFromService(pointData.properties.filename);
                  }
                  break;
                case 'soilpit':
                  break;
                case 'observation':
                  break;
              }
            }
          }

          if (processedInfo.selectedFeature?.properties?.poly_id) {
            getComponents(processedInfo.selectedFeature.properties.poly_id);
          }
        } catch (error) {
          console.error('Error processing feature:', error);
          // Only clear state if there's an actual error, not just new features
          if (features.length === 0) {
            queueMicrotask(() => clearState());
          }
        }
      };

      processFeature();
    } else {
      queueMicrotask(() => clearState());
    }
  }, [features]);

  const formatArea = (hectares?: number) => {
    if (!hectares) return '';
    const acres = hectares * 2.47105;
    return `${acres.toFixed(0)} acres (${hectares} ha)`;
  };

  const getAgCapModToText = (modifier: string) => {
    const modChars = modifier.split('');
    let modStrings = '';
    const dict: { [key: string]: string } = {
      C: 'Adverse climate',
      D: 'Undesirable soils structure and/or low permeability',
      E: 'Erosion',
      F: 'Low fertility',
      I: 'Inundation by streams or lakes',
      M: 'Moisture limitations',
      N: 'Salinity',
      P: 'Stoniness',
      R: 'Consolidated bedrock',
      S: 'Combination of subclasses',
      T: 'Topography',
      W: 'Excess water',
      X: 'Cumulative effect of two or more adverse characteristics',
    };

    modChars.forEach((m) => {
      if (m !== ' ') {
        modStrings += dict[m];
        modStrings += '. ';
      }
    });

    return modStrings ? modStrings : 'No modifier';
  };

  const letterToTexture = (letter: string) => {
    switch (letter) {
      case 'C':
        return 'Clay';
      case 'CL':
        return 'Clay loam';
      case 'CS':
        return 'Coarse sand';
      case 'FCL':
        return 'Fine sandy clay loam';
      case 'FL':
        return 'Fine sandy loam';
      case 'FS':
        return 'Fine sand';
      case 'HC':
        return 'Heavy clay';
      case 'L':
        return 'Loam';
      case 'O':
        return 'Organic';
      case 'S':
        return 'Sand';
      case 'SCL':
        return 'Sandy clay loam';
      case 'SIC':
        return 'Silty clay';
      case 'SICL':
        return 'Silty clay loam';
      case 'SIL':
        return 'Silty loam';
      case 'SL':
        return 'Sandy loam';
      case 'VL':
        return 'Very fine sandy loam';
      case 'GS':
        return 'Gravelly sand';
      case 'LS':
        return 'Loamy sand';
      case 'GLS':
        return 'Gravelly loamy sand';
      case 'LFS':
        return 'Loamy fine sand';
      case 'GSL':
        return 'Gravelly sandy loam';
      case 'VCL':
        return 'Very fine sandy clay loam';
      case 'GL':
        return 'Gravelly loam';
      case 'U':
        return 'Unclassified';
    }
  }

  return (
    <>
      <Box
        w={{ base: 'full', lg: '30rem' }}
        minW={{ base: 'full', lg: '30rem' }}
        position='fixed'
        top='0'
        right='0'
        h='100vh'
        bg='white'
        paddingBottom={{ base: 'calc(6rem + env(safe-area-inset-bottom, 0px))', lg: '6' }}
        borderLeft='1px solid'
        borderColor='bg.muted'
        overflowY='auto'
        overscrollBehavior='contain'
        className='no-bg-scrollbar'
        display={clickedCoordinate ? 'block' : 'none'}
        zIndex={1001}
        transition='transform 0.3s ease-in-out'
        transform={clickedCoordinate ? 'translateX(0)' : 'translateX(100%)'}
      >
        <Box p='4' borderBottom='1px solid' borderColor='bg.muted' display='flex' justifyContent='space-between' alignItems='center'>
          <Text fontSize='lg' fontWeight='semibold'>Polygon Information</Text>
          <CloseButton size='md' onClick={handleClose} />
        </Box>

        <Box p='4' pb={{ base: '8', lg: '4' }}>
          <Tabs.Root value={tab} onValueChange={(e) => {
            setTab(e.value);
            saveTabToStorage(e.value);
          }} variant='outline' size='md'>
            <Tabs.List>
              <Tabs.Trigger value='polygon'>Polygon</Tabs.Trigger>
              <Tabs.Trigger value='map'>Map</Tabs.Trigger>
              <Tabs.Trigger value='component'>Component</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value='polygon'>
              <Stack gap={2} p='2'>
                {isLoading && 
                  <Flex justifyContent='center' alignItems='center' h='100%' py='4'>
                    <ProgressCircle.Root value={null} size="lg">
                      <ProgressCircle.Circle css={{ "--thickness": "3px" }}>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range />
                      </ProgressCircle.Circle>
                    </ProgressCircle.Root>
                  </Flex>
                }
                {polygonInfoProperties && (
                  <Stack gap={2}>
                    {errataNote && errataNote.note && (
                      <Text fontSize='sm' color='red.500'>
                        <Box as='strong'>Note: </Box>
                        {errataNote.note}
                      </Text>
                    )}

                    {polygonInfoProperties?.poly_id && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Polygon ID: </Box>
                        {polygonInfoProperties.poly_id}
                      </Text>
                    )}
                    <Box>
                      {polygonInfoProperties?.polygon_label && (
                        <Text fontSize='sm' mb='2'>
                          <Box as='strong'>Polygon Label: </Box>
                          {polygonInfoProperties.polygon_label}
                        </Text>
                      )}
                      {polygonInfoProperties?.mapunitDescription && (
                        <Text fontSize='sm' mb='2'>
                          {polygonInfoProperties.mapunitDescription}
                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Soil classes</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofcanada.ca/orders/orders.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on soil classes
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Text>
                      )}
                      {polygonInfoProperties?.assocDescription && (
                        <Text fontSize='sm' mb='2'>
                          {polygonInfoProperties.assocDescription}
                          <Dialog.Root>
                          <Dialog.Trigger asChild>
                            <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                          </Dialog.Trigger>
                          <Portal>
                            <Dialog.Backdrop />
                            <Dialog.Positioner>
                              <Dialog.Content>
                                <Dialog.Header>
                                  <Dialog.Title>Mode of deposition</Dialog.Title>
                                  <Dialog.CloseTrigger asChild>
                                    <CloseButton />
                                  </Dialog.CloseTrigger>
                                </Dialog.Header>
                                <Dialog.Body>
                                  <Link
                                    href='https://soilsofsask.ca/soil-survey-soil-characteristics/surface-deposits.php'
                                    variant='underline'
                                    color='blue.500'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    Click for more info on surface deposits
                                  </Link>
                                </Dialog.Body>
                              </Dialog.Content>
                            </Dialog.Positioner>
                          </Portal>
                          </Dialog.Root>
                        </Text>
                      )}
                    </Box>

                    {polygonInfoProperties?.text1 && (
                      <Box>
                        <Flex>
                          <Text fontSize='sm' marginRight='2'>
                            <Box as='strong'>Surface Texture: </Box>
                          </Text>

                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Text fontSize='sm' color='blue.500' marginRight='2' cursor='pointer' _hover={{ textDecoration: 'underline' }}>
                                {polygonInfoProperties.text2 && polygonInfoProperties.text2 !== polygonInfoProperties.text1
                                  ? `${letterToTexture(polygonInfoProperties.text1)} to ${letterToTexture(polygonInfoProperties.text2)}`
                                  : letterToTexture(polygonInfoProperties.text1)}
                              </Text>
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Texture Triangle</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Image src="/texture-triangle.png" alt='Texture Triangle' />
                                  </Dialog.Body>
                                  <Dialog.Footer>
                                    <Dialog.ActionTrigger asChild>
                                      <Button variant='outline' colorScheme='blue'>Close</Button>
                                    </Dialog.ActionTrigger>
                                  </Dialog.Footer>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>

                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Soil texture</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofsask.ca/soil-survey-soil-characteristics/soil-texture.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on soil texture
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Flex>
                      </Box>
                    )}

                    {polygonInfoProperties?.slope_description && (
                      <Box>
                        <Text fontSize='sm'>
                          <Box as='strong'>Slope Description: </Box>
                          {polygonInfoProperties.slope_description}
                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Slope Description</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofsask.ca/soil-survey-soil-characteristics/slope.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on slope description
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Text>
                      </Box>
                    )}

                    {polygonInfoProperties?.surfexname && (
                      <Box>
                        <Text fontSize='sm'>
                          <Box as='strong'>Surface Expression: </Box>
                          {polygonInfoProperties.surfexname}
                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Surface Expression</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofsask.ca/soil-survey-soil-characteristics/surface-forms.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on surface expression
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Text>
                      </Box>
                    )}

                    {polygonInfoProperties?.salsym && (
                      <Box>
                        <Text fontSize='sm'>
                          <Box as='strong'>Salinity Class: </Box>
                          {polygonInfoProperties.salsym}
                        </Text>
                      </Box>
                    )}

                    {polygonInfoProperties?.salDesc && (
                      <Box>
                        <Text fontSize='sm'>
                          Salinity affect on productivity: {polygonInfoProperties.salDesc}
                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Backdrop />
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Salinity class</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofsask.ca/soil-survey-soil-characteristics/salinity-class.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on salinity class
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Text>
                      </Box>
                    )}

                    <Box>
                      {polygonInfoProperties?.capblty && (
                        <Text fontSize='sm' mb='2'>
                          <Box as='strong'>Ag Capability:</Box>
                          {polygonInfoProperties.capblty}
                        </Text>
                      )}

                      {polygonInfoProperties?.prettyAgCap && (
                        <Box mb='2'>
                          {polygonInfoProperties.prettyAgCap.map((agC: any, index: number) => (
                            <Box key={index}>
                              <Text fontSize='sm'>
                                {agC.percent}% class: {agC.class}
                              </Text>
                              <Text fontSize='sm' ml={2}>{getAgCapModToText(agC.modifier)}</Text>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {polygonInfoProperties?.agCapDesc && (
                        <Text fontSize='sm' mb='2'>
                          {polygonInfoProperties.agCapDesc}
                          <Dialog.Root>
                            <Dialog.Trigger asChild>
                              <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                            </Dialog.Trigger>
                            <Portal>
                              <Dialog.Positioner>
                                <Dialog.Content>
                                  <Dialog.Header>
                                    <Dialog.Title>Ag Capability</Dialog.Title>
                                    <Dialog.CloseTrigger asChild>
                                      <CloseButton />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Header>
                                  <Dialog.Body>
                                    <Link
                                      href='https://soilsofsask.ca/soil-survey-soil-characteristics/agricultural-capability.php'
                                      variant='underline'
                                      color='blue.500'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      Click for more info on agriculture capability
                                    </Link>
                                  </Dialog.Body>
                                </Dialog.Content>
                              </Dialog.Positioner>
                            </Portal>
                          </Dialog.Root>
                        </Text>
                      )}
                    </Box>

                    <Box mb='2'>
                      {polygonInfoProperties?.cphsym && (
                        <Text fontSize='sm' mb='2'>
                          <Box as='strong'>pH Class: </Box>
                          {polygonInfoProperties.cphsym}
                        </Text>
                      )}
                      <MapProgressBar phProportions={phProportions} />
                    </Box>

                    {polygonInfoProperties?.stonename && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Stoniness: </Box>
                        {polygonInfoProperties.stonename}
                      </Text>
                    )}

                    
                    <Text fontSize='sm'>
                      <Box as='strong'>Initial Irrigation: </Box>
                      { irrigDegreeoflimitaion ? irrigDegreeoflimitaion : 'no data'}
                    </Text>

                    {polygonInfoProperties?.hectares && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Area: </Box>
                          {formatArea(polygonInfoProperties.hectares)}
                      </Text>
                    )}

                    {polygonInfoProperties?.poly_id && (
                      <Button
                        onClick={() => {
                          window.location.href = `mailto:sksis_support@usask.ca?subject=Data feedback in polygon ${polygonInfoProperties.poly_id}&body=Polygon ID:${polygonInfoProperties.poly_id}%0D%0ALocation: ${clickedCoordinate?.[0]} ${clickedCoordinate?.[1]}%0D%0A%0D%0APlease give detailed feedback:%0D%0A%0D%0AThanks!`;
                        }}
                        bg='blue.500'
                        color='white'
                        _hover={{ bg: 'blue.600' }}
                        variant='outline'
                        mt='5'
                        mb={{ base: '8', lg: '2' }}
                      >
                        Provide Feedback
                      </Button>
                    )}
                  </Stack>
                )}
                {!isLoading && !polygonInfoProperties && (
                  <Text fontSize='sm'>No polygon information available.</Text>
                )}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value='map'>
              <Stack gap={2} p='2'>
                {isLoading && 
                  <Flex justifyContent='center' alignItems='center' h='100%' py='4'>
                    <ProgressCircle.Root value={null} size="lg">
                      <ProgressCircle.Circle css={{ "--thickness": "3px" }}>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range />
                      </ProgressCircle.Circle>
                    </ProgressCircle.Root>
                  </Flex>
                }
                {!isLoading && (
                  <>
                    <Text fontSize='sm'>
                      <Box as='strong'>Mouse position (WGS84): </Box>
                      {mouseCoordinate?.lat}&deg; {mouseCoordinate?.lng}&deg;
                    </Text>
                    {clickedCoordinate ? (
                      <Text fontSize='sm'>
                        <b>Click coordinate:</b> {clickedCoordinate[0].toFixed(4)}{' '}
                        {clickedCoordinate[1].toFixed(4)}
                      </Text>
                      ) : (
                        <Text fontSize='sm'>No coordinate selected.</Text>
                      )
                    }
                    {sectionInfo && (
                      <Text fontSize='sm'>
                        <b>Click LLD:</b> Sec:{sectionInfo.sect}-{sectionInfo.ptwp}-{sectionInfo.prge} W{sectionInfo.pmer}
                      </Text>
                    )}
                    <Text fontSize='sm'>
                      <Box as='strong'>Scale: </Box>
                      {scaleString}
                    </Text>
                    {ecoRegionInfo && ecoRegionInfo.ecoregion && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Ecoregion (range land modified): </Box>
                        {ecoRegionInfo.ecoregion}
                      </Text>
                    )}
                    {ecoSiteInfo && ecoSiteInfo.domecosite && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Dominant Ecosite: </Box>
                        {ecoSiteInfo.domecosite}
                        <Dialog.Root>
                          <Dialog.Trigger asChild>
                            <Icon as={FaInfoCircle} mx='1' color='blue.500' cursor='pointer' />
                          </Dialog.Trigger>
                          <Portal>
                            <Dialog.Backdrop />
                            <Dialog.Positioner>
                              <Dialog.Content>
                                <Dialog.Header>
                                  <Dialog.Title>Ecosite Information</Dialog.Title>
                                  <Dialog.CloseTrigger asChild>
                                    <CloseButton />
                                  </Dialog.CloseTrigger>
                                </Dialog.Header>
                                <Dialog.Body>
                                  <Text fontSize='sm' fontWeight='bold' mb='1'>
                                    Ecosite Information
                                  </Text>
                                  <Text fontSize='sm'>
                                    Ecosite classification for rangeland management
                                  </Text>
                                </Dialog.Body>
                              </Dialog.Content>
                            </Dialog.Positioner>
                          </Portal>
                        </Dialog.Root>
                      </Text>
                    )}
                    {ecoSiteInfo && ecoSiteInfo.ecosite4 && (
                      <Text fontSize='sm'>
                        <Box as='strong'>Ecosite (4): </Box>
                        {ecoSiteInfo.ecosite4}
                      </Text>
                    )}
                    {surficialGeologyInfo && surficialGeologyInfo.code && (
                      <Table.Root size='sm'>
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell verticalAlign='top' padding='0' border='none'>
                              <b>Surficial Geology:</b>
                            </Table.Cell>
                            <Table.Cell padding='0' border='none'>
                              {surficialGeologyInfo?.code} <br />
                              {surficialGeologyInfo?.main_envir}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table.Root>
                    )}
                  </>
                )}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value='component'>
              <Stack gap='4' pb='4' p='2'>
                {isLoading && 
                  <Flex justifyContent='center' alignItems='center' h='100%' py='4'>
                    <ProgressCircle.Root value={null} size="lg">
                      <ProgressCircle.Circle css={{ "--thickness": "3px" }}>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range />
                      </ProgressCircle.Circle>
                    </ProgressCircle.Root>
                  </Flex>
                }
                {!isLoading && polygonInfoProperties && components.length > 0 && (
                  <>
                    <DonuteChart 
                      data={components.map((comp: any, index: number) => ({
                        name: comp.soilname || 'Unknown',
                        value: parseFloat(comp.percent) || 0,
                        color: colors[index]
                      }))} 
                    />

                    {components.map((comp: any, index: number) => (
                      <Box key={index} bg='gray.50' p={3} borderRadius='md'>
                        <Text fontSize='sm'>
                          <Box as='strong'>Soil name:</Box> {comp.soilname}
                        </Text>
                        <Text fontSize='sm'>
                          <Box as='strong'>% of polygon:</Box> {comp.percent}
                        </Text>
                        <Text fontSize='sm'>
                          <Box as='strong'>Classification:</Box> {comp.classification}
                        </Text>
                        <Text fontSize='sm'>
                          <Box as='strong'>Parent material:</Box> {comp.parent_material}
                        </Text>
                      </Box>
                    ))}

                    <Button
                      onClick={() => {
                        window.location.href = `mailto:sksis_support@usask.ca?subject=Data feedback with components of polygon ${polygonInfoProperties?.poly_id}&body=Polygon ID:${polygonInfoProperties?.poly_id}%0D%0ALocation: ${clickedCoordinate?.[0]} ${clickedCoordinate?.[1]}%0D%0A%0D%0APlease give detailed feedback:%0D%0A%0D%0AThanks!`;
                      }}
                      bg='blue.500'
                      color='white'
                      _hover={{ bg: 'blue.600' }}
                      variant='outline'
                      mb={{ base: '8', lg: '2' }}
                    >
                      Provide Feedback
                    </Button>
                  </>
                )}

                {imageUrl && (
                  <Box>
                    <Text fontSize='sm' fontWeight='bold'>
                      Image:
                    </Text>
                    <img src={imageUrl} alt='Point data' style={{ maxWidth: '100%' }} />
                  </Box>
                )}
                {pdfUrl && (
                  <Box>
                    <Text fontSize='sm' fontWeight='bold'>
                      PDF Document:
                    </Text>
                    <a href={pdfUrl} target='_blank' rel='noopener noreferrer'>
                      <Button
                        bg='blue.500'
                        color='white'
                        _hover={{ bg: 'blue.600' }}
                        variant='outline'
                      >
                        Open PDF
                      </Button>
                    </a>
                  </Box>
                )}
                {soilPitId && (
                  <Box>
                    <Text fontSize='sm' fontWeight='bold'>
                      Soil Pit ID:
                    </Text>
                    <Text fontSize='sm'>{soilPitId}</Text>
                    {soilPitData && (
                      <Button
                        mt={2}
                        bg='green.500'
                        color='white'
                        _hover={{ bg: 'green.600' }}
                        onClick={() => setShowEnhancedInfo(true)}
                      >
                        View Detailed Soil Information
                      </Button>
                    )}
                  </Box>
                )}
              </Stack>
            </Tabs.Content>
          </Tabs.Root>
        </Box>

        {/* Enhanced Soil Information Modal */}
        {showEnhancedInfo && soilPitData && (
          <Portal>
            <Box
              position='fixed'
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg='blackAlpha.600'
              zIndex={2000}
              display='flex'
              alignItems='center'
              justifyContent='center'
              onClick={() => setShowEnhancedInfo(false)}
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                maxW='800px'
                maxH='90vh'
                overflow='auto'
                bg='white'
                borderRadius='lg'
                boxShadow='xl'
              >
                <EnhancedSoilInfo soilData={soilPitData} onClose={() => setShowEnhancedInfo(false)} />
              </Box>
            </Box>
          </Portal>
        )}
      </Box>
    </>
  );
};

export default MapDrawer;
